import PropTypes from 'prop-types';
import React from 'react';

import { ReactComponent as InfoIcon } from '../../../../assets/images/info.svg';
import { shortMonthDateString } from '../../../../lib/dateFormatter';
import { formatNumber, formatCurrencyNumber } from '../../../../lib/numberFormatter';
import ToolTip from '../../../ToolTip';
import './index.scss';
import WhiteCard from '../../../WhiteCard';

const FullOfferDetails = ({
  currency,
  initialPaymentDate,
  firstRepaymentDate,
  loanAmount,
  adminFee,
  adminFeePercentage,
  selectedOffer,
  processingFeeValue,
  cosignerSelection,
  applicationID,
  expiryDate,
}) => {
  const formattedFirstInitialPaymentDate = shortMonthDateString(new Date(initialPaymentDate));
  const formattedFirstRepaymentDate = shortMonthDateString(new Date(firstRepaymentDate));
  const fullLoanAmount = formatCurrencyNumber(parseFloat(loanAmount) + parseFloat(adminFee));

  return (
    <div className="full-offer-details">
      <div className="full-offer-details__section">
        <section>
          <h3 className="section-title">Review Offer</h3>
          <WhiteCard className="review-offer__white-card">
            <div className="full-offer-details__list h6">
              <ul>
                <li>
                  <div className="full-offer-details__list__label h6">Loan amount</div>
                  <div
                    className="full-offer-details__list__value h6"
                    data-testid="loan-amount-value"
                  >
                    {currency}&nbsp;<span className="h4">{formatCurrencyNumber(loanAmount)}</span>
                  </div>
                </li>
                <li>
                  <div className="full-offer-details__list__label h6">Interest rate</div>
                  <div
                    className="full-offer-details__list__value h4"
                    data-testid="interest-rate-value"
                  >
                    {formatNumber(selectedOffer.interest_rate)}%
                  </div>
                </li>
                <li>
                  <div className="full-offer-details__list__label h6">Loan term</div>
                  <div className="full-offer-details__list__value h4" data-testid="loan-term-value">
                    {selectedOffer.term_in_months / 12}&nbsp;years
                  </div>
                </li>
                <li>
                  <div className="full-offer-details__list__label h6">
                    Admin fee ({adminFeePercentage}%)
                    <ToolTip
                      icon={<InfoIcon />}
                      header="Admin fee"
                      text="To be added to your loan amount and not paid upfront"
                    />
                  </div>
                  <div className="full-offer-details__list__value h6" data-testid="admin-fee-value">
                    {currency}&nbsp;<span className="h4">{formatNumber(adminFee)}</span>
                  </div>
                </li>
                <li>
                  <div className="full-offer-details__list__label h6">Total amount of credit</div>
                  <div
                    className="full-offer-details__list__value h6"
                    data-testid="total-credit-value"
                  >
                    {currency}&nbsp;<span className="h4">{fullLoanAmount}</span>
                  </div>
                </li>
                <li>
                  <div className="full-offer-details__list__label h6">Total interest payable</div>
                  <div
                    className="full-offer-details__list__value h6"
                    data-testid="total-interest-value"
                  >
                    {currency}&nbsp;
                    <span className="h4">
                      {formatCurrencyNumber(Math.ceil(selectedOffer.total_interest_charge))}
                    </span>
                  </div>
                </li>
                <li>
                  <div className="full-offer-details__list__label h6">Total repayable</div>
                  <div
                    className="full-offer-details__list__value h6"
                    data-testid="total-repayable-value"
                  >
                    {currency}&nbsp;
                    <span className="h4">
                      {formatCurrencyNumber(Math.ceil(selectedOffer.total_repayable_amount))}
                    </span>
                  </div>
                </li>
                {processingFeeValue && (
                  <li>
                    <div className="full-offer-details__list__label h6">
                      Processing fee
                      <ToolTip
                        icon={<InfoIcon />}
                        header="Processing fee"
                        text="To be paid before your loan is disbursed"
                      />
                    </div>
                    <div
                      className="full-offer-details__list__value h6"
                      data-testid="processing-fee-value"
                    >
                      {currency}&nbsp;
                      <span className="h4">{formatCurrencyNumber(processingFeeValue)}</span>
                    </div>
                  </li>
                )}
                <hr />
                {cosignerSelection === 'cosigner' ? (
                  <>
                    <li>
                      <div className="full-offer-details__list__label h6">
                        Initial monthly payment
                      </div>
                      <div
                        className="full-offer-details__list__value h6"
                        data-testid="instalment-amount-value"
                      >
                        {currency}&nbsp;
                        <span className="h4">
                          {formatCurrencyNumber(selectedOffer.grace_payment_amount, 2, 2)}
                        </span>
                      </div>
                    </li>
                    <li>
                      <div className="full-offer-details__list__label h6">
                        First initial payment
                      </div>
                      <div
                        className="full-offer-details__list__value h6"
                        data-testid="instalment-amount-value"
                      >
                        <span className="h4">{formattedFirstInitialPaymentDate}</span>
                      </div>
                    </li>
                    <li>
                      <div className="full-offer-details__list__label h6">
                        Subsequent monthly payment
                      </div>
                      <div
                        className="full-offer-details__list__value h6"
                        data-testid="instalment-amount-value"
                      >
                        {currency}&nbsp;
                        <span className="h4">
                          {formatCurrencyNumber(Math.ceil(selectedOffer.monthly_repayment))}
                        </span>
                      </div>
                    </li>
                    <li>
                      <div className="full-offer-details__list__label h6">
                        First subsequent payment
                      </div>
                      <div
                        className="full-offer-details__list__value h6"
                        data-testid="instalment-amount-value"
                      >
                        <span className="h4">{formattedFirstRepaymentDate}</span>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <div className="full-offer-details__list__label h6">Instalment amount</div>
                      <div
                        className="full-offer-details__list__value h6"
                        data-testid="instalment-amount-value"
                      >
                        {currency}&nbsp;
                        <span className="h4">
                          {formatCurrencyNumber(selectedOffer.monthly_repayment, 2, 2)}
                        </span>
                      </div>
                    </li>
                    <li>
                      <div className="full-offer-details__list__label h6">First repayment</div>
                      <div
                        className="full-offer-details__list__value h4"
                        data-testid="first-repayment-value"
                      >
                        {formattedFirstRepaymentDate}
                      </div>
                    </li>
                  </>
                )}
                <hr />
                <li>
                  <div className="full-offer-details__list__label h6">
                    Annual Percentage Rate
                    <ToolTip
                      icon={<InfoIcon />}
                      header="Annual Percentage Rate"
                      text="The total cost of borrowing can be expressed as APR (Annual Percentage Rate). APR reflects interest and all mandatory <u>fees</u> and the effects of any compounding."
                    />
                  </div>
                  <div className="full-offer-details__list__value h4" data-testid="apr-value">
                    {formatNumber(selectedOffer.apr)}%
                  </div>
                </li>
              </ul>
            </div>
          </WhiteCard>
        </section>
      </div>
      <div className="full-offer-details__text-right">
        <span className="h6 sub-heading" data-testid="application-id">
          Application number: {applicationID}
        </span>
        <br />
        <span className="h6 sub-heading" data-testid="expiry-date">
          Valid until {shortMonthDateString(new Date(expiryDate))}
        </span>
      </div>
    </div>
  );
};

FullOfferDetails.propTypes = {
  currency: PropTypes.string.isRequired,
  firstRepaymentDate: PropTypes.string.isRequired,
  loanAmount: PropTypes.string.isRequired,
  adminFee: PropTypes.string.isRequired,
  adminFeePercentage: PropTypes.string.isRequired,
  selectedOffer: PropTypes.shape({
    monthly_repayment: PropTypes.string.isRequired,
    term_in_months: PropTypes.number.isRequired,
    total_interest_charge: PropTypes.string.isRequired,
    total_repayable_amount: PropTypes.string.isRequired,
    interest_rate: PropTypes.string.isRequired,
    apr: PropTypes.string.isRequired,
    grace_payment_amount: PropTypes.string.isRequired,
  }).isRequired,
  processingFeeValue: PropTypes.string,
  cosignerSelection: PropTypes.string.isRequired,
  initialPaymentDate: PropTypes.string,
  applicationID: PropTypes.string.isRequired,
  expiryDate: PropTypes.string.isRequired,
};

export default FullOfferDetails;
